// Urlaubsanträge entscheiden — hier sitzt Sebastian.
//
// Zwei Dinge muss er sehen, bevor er zusagt: Wie viel Urlaub hat die Person noch,
// und ist in dem Zeitraum sonst schon jemand weg. Ohne das ist eine Zusage
// geraten.

import { useMemo, useState } from "react";
import {
  useOffeneAntraege,
  useAlleAbwesenheiten,
  useEntscheiden,
  artLabel,
  zeitraum,
  type Abwesenheit,
} from "@/lib/urlaub";
import { LogoLoader } from "@/components/LogoLoader";
import { Button, Card, EmptyState, Input, Segmented, Sheet, cn } from "@/design-system/ui";

type Sicht = "offen" | "jahr";

export function AntraegeTab() {
  const [sicht, setSicht] = useState<Sicht>("offen");
  const jahr = new Date().getFullYear();

  const { data: offen = [], isLoading: laedtOffen } = useOffeneAntraege();
  const { data: alle = [], isLoading: laedtAlle } = useAlleAbwesenheiten(jahr);

  const liste = sicht === "offen" ? offen : alle;
  const laedt = sicht === "offen" ? laedtOffen : laedtAlle;

  const [entscheiden, setEntscheiden] = useState<Abwesenheit | null>(null);

  return (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        <Segmented
          className="mb-4"
          value={sicht}
          onChange={setSicht}
          options={[
            { value: "offen", label: offen.length ? `Offen · ${offen.length}` : "Offen" },
            { value: "jahr", label: String(jahr) },
          ]}
        />

        {laedt ? (
          <div className="flex justify-center py-16">
            <LogoLoader size="md" label="Anträge werden geladen" />
          </div>
        ) : liste.length === 0 ? (
          <EmptyState
            title={sicht === "offen" ? "Nichts zu entscheiden" : "Keine Abwesenheiten"}
            hint={
              sicht === "offen"
                ? "Alle Anträge sind bearbeitet."
                : `${jahr} hat noch niemand Urlaub genommen.`
            }
          />
        ) : (
          <ul className="space-y-2">
            {liste.map((a) => (
              <li key={a.id}>
                <AntragKarte
                  a={a}
                  alle={alle}
                  onEntscheiden={() => setEntscheiden(a)}
                />
              </li>
            ))}
          </ul>
        )}
      </div>

      {entscheiden && (
        <EntscheidungSheet
          a={entscheiden}
          alle={alle}
          onClose={() => setEntscheiden(null)}
        />
      )}
    </>
  );
}

/** Wer ist im selben Zeitraum noch weg? Das entscheidet, ob man zusagen kann. */
function gleichzeitig(a: Abwesenheit, alle: Abwesenheit[]) {
  return alle.filter(
    (x) =>
      x.id !== a.id &&
      x.status === "genehmigt" &&
      x.profile_id !== a.profile_id &&
      x.von <= a.bis &&
      x.bis >= a.von
  );
}

function AntragKarte({
  a,
  alle,
  onEntscheiden,
}: {
  a: Abwesenheit;
  alle: Abwesenheit[];
  onEntscheiden: () => void;
}) {
  const offen = a.status === "beantragt";
  const kollision = gleichzeitig(a, alle);

  return (
    <Card
      className={cn(
        "p-4",
        a.status === "genehmigt" && "border-gruen-500/25",
        a.status === "abgelehnt" && "border-danger-bg/25 opacity-70",
        a.status === "zurueckgezogen" && "opacity-50",
        offen && "border-warn-bg/40 bg-warn-bg/5"
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-[15px] font-medium text-ink-100">
            {a.person?.full_name ?? "Ohne Namen"}
          </div>
          <div className="mt-0.5 text-sm text-ink-400">
            {artLabel(a.art)} · {zeitraum(a.von, a.bis, a.halber_tag)}
          </div>
        </div>

        <div className="shrink-0 text-right">
          <div className="text-[15px] font-semibold tabular-nums text-ink-50">
            {Number(a.arbeitstage).toLocaleString("de-DE")}
            <span className="ml-1 text-xs font-normal text-ink-400">Tage</span>
          </div>
        </div>
      </div>

      {a.begruendung && <p className="mt-2 text-sm text-ink-400">{a.begruendung}</p>}

      {offen && kollision.length > 0 && (
        <p className="mt-2 rounded-lg border border-warn-bg/40 bg-warn-bg/10 px-3 py-2 text-sm text-warn-fg">
          Gleichzeitig weg:{" "}
          {kollision.map((k) => k.person?.full_name ?? "?").join(", ")}
        </p>
      )}

      {a.status !== "beantragt" && a.entscheidung_kommentar && (
        <p className="mt-2 rounded-lg bg-ink-900 px-3 py-2 text-sm text-ink-300">
          {a.entscheidung_kommentar}
        </p>
      )}

      {offen && (
        <Button variant="primary" size="sm" className="mt-3 w-full" onClick={onEntscheiden}>
          Entscheiden
        </Button>
      )}
    </Card>
  );
}

function EntscheidungSheet({
  a,
  alle,
  onClose,
}: {
  a: Abwesenheit;
  alle: Abwesenheit[];
  onClose: () => void;
}) {
  const entscheiden = useEntscheiden();
  const [kommentar, setKommentar] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);

  const kollision = gleichzeitig(a, alle);

  // Wie viel hat die Person dieses Jahr schon genommen — und was bleibt, wenn
  // dieser Antrag durchgeht?
  const konto = useMemo(() => {
    const anspruch = Number(a.person?.urlaubstage_jahr ?? 25);
    const genommen = alle
      .filter((x) => x.profile_id === a.profile_id && x.art === "urlaub" && x.status === "genehmigt")
      .reduce((s, x) => s + Number(x.arbeitstage), 0);
    const dieser = a.art === "urlaub" ? Number(a.arbeitstage) : 0;
    return { anspruch, genommen, danach: anspruch - genommen - dieser };
  }, [a, alle]);

  const buchen = async (genehmigt: boolean) => {
    setFehler(null);
    try {
      await entscheiden.mutateAsync({ id: a.id, genehmigt, kommentar });
      onClose();
    } catch (e) {
      setFehler((e as Error).message);
    }
  };

  return (
    <Sheet
      open
      onClose={onClose}
      title={a.person?.full_name ?? "Antrag"}
      footer={
        <div className="space-y-2">
          {fehler && (
            <p className="rounded-lg border border-danger-bg/30 bg-danger-bg/10 px-3 py-2 text-sm text-danger-fg">
              {fehler}
            </p>
          )}

          <Button
            variant="primary"
            size="lg"
            className="w-full"
            loading={entscheiden.isPending}
            onClick={() => buchen(true)}
          >
            Genehmigen
          </Button>
          <Button
            variant="danger"
            className="w-full"
            loading={entscheiden.isPending}
            onClick={() => buchen(false)}
          >
            Ablehnen
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wider text-ink-400">
            {artLabel(a.art)}
          </div>
          <div className="mt-1 text-xl font-semibold text-ink-50">
            {zeitraum(a.von, a.bis, a.halber_tag)}
          </div>
          <div className="mt-1 text-sm tabular-nums text-ink-400">
            {Number(a.arbeitstage).toLocaleString("de-DE")} Arbeitstage
          </div>
        </Card>

        {a.begruendung && (
          <p className="rounded-lg bg-ink-900 px-3 py-2.5 text-sm text-ink-300">{a.begruendung}</p>
        )}

        {/* Urlaubskonto — ohne das wäre eine Zusage geraten. */}
        {a.art === "urlaub" && (
          <Card className="space-y-1.5 p-4">
            <Zeile label="Jahresanspruch" wert={`${konto.anspruch} Tage`} />
            <Zeile label="Bereits genommen" wert={`${konto.genommen.toLocaleString("de-DE")} Tage`} />
            <div className="!mt-2 border-t border-ink-700 pt-2">
              <Zeile
                label="Rest nach diesem Antrag"
                wert={`${konto.danach.toLocaleString("de-DE")} Tage`}
                warnung={konto.danach < 0}
              />
            </div>
          </Card>
        )}

        {kollision.length > 0 && (
          <div className="rounded-lg border border-warn-bg/40 bg-warn-bg/10 p-3">
            <p className="text-sm font-medium text-warn-fg">Im selben Zeitraum ist weg:</p>
            <ul className="mt-1 space-y-0.5">
              {kollision.map((k) => (
                <li key={k.id} className="text-sm text-warn-fg">
                  {k.person?.full_name ?? "?"} · {zeitraum(k.von, k.bis, k.halber_tag)}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div>
          <span className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-ink-400">
            Antwort <span className="font-normal normal-case text-ink-500">optional</span>
          </span>
          <Input
            value={kommentar}
            onChange={(e) => setKommentar(e.target.value)}
            placeholder="z. B. Passt — schöne Ferien!"
          />
        </div>
      </div>
    </Sheet>
  );
}

function Zeile({
  label,
  wert,
  warnung,
}: {
  label: string;
  wert: string;
  warnung?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-sm text-ink-400">{label}</span>
      <span
        className={cn(
          "font-medium tabular-nums",
          warnung ? "text-danger-fg" : "text-ink-100"
        )}
      >
        {wert}
      </span>
    </div>
  );
}
